
// © Microsoft Corporation. All rights reserved.

import { RenderCache, RenderCacheKeys } from "./RenderCache";
import { renderCacheKeyOfNormalizedKeys, riverRcClassName } from "./Constants";

let renderCacheObject: RenderCache;

/**
 * Function to get render cache if it's valid.
 * @returns Render cache object if it exists and is valid otherwise null.
 */
export function getRenderCache(): RenderCache {
    if (renderCacheObject) {
        return renderCacheObject;
    }

    if (!(window && window.localStorage)) {
        return null;
    }

    // get all normalized render cache keys from local storage
    let renderCacheKeys: RenderCacheKeys;
    const renderCacheStoredKeys = window.localStorage.getItem(renderCacheKeyOfNormalizedKeys);
    try {
        renderCacheKeys = renderCacheStoredKeys && JSON.parse(renderCacheStoredKeys);
    } catch (error) {
        return null; // keys not parsable.
    }

    if (!renderCacheKeys || !renderCacheKeys.keyn || !renderCacheKeys.expn || !renderCacheKeys.frcn || !renderCacheKeys.jssn) {
        return null;
    }

    const cacheExpirationTimestamp = window.localStorage.getItem(renderCacheKeys.expn);
    if (!cacheExpirationTimestamp) {
        return null; // Cache Expiration Timestamp not exist
    }

    const parsedCacheExpirationTimestamp = parseInt(cacheExpirationTimestamp);
    if (!parsedCacheExpirationTimestamp) {
        return null; // Cache Expiration Timestamp broken.
    }

    const currentTimestamp: number = new Date().getTime();
    if (currentTimestamp >= parsedCacheExpirationTimestamp) {
        return null; // cache expired.
    }

    const renderCacheStorage = window.localStorage.getItem(renderCacheKeys.keyn);
    if (!renderCacheStorage) {
        return null; // Cache object is missing.
    }

    try {
        renderCacheObject = JSON.parse(renderCacheStorage);
    } catch (error) {
        return null; // Cache object not parsable.
    }

    if (!renderCacheObject || !renderCacheObject.dom || !renderCacheObject.batch_css) {
        renderCacheObject = null; // Cache object is broken
    }

    return renderCacheObject;
}

/**
 * Batch All styles into one object.
 */
export function batchCSS(): string {
    let batchCss = "";

    // Enumerate and write out all of the JSS styles
    const styles = document.getElementsByTagName("style");
    const length = styles.length;

    for (let i = 0; i < length; i++) {
        // Skip any styles that do not have a data-jss attribute to always pick newer styles added by jss
        if (styles[i].dataset.jss != null) {
            batchCss += styles[i].innerHTML;
        }
    }

    return batchCss;
}

/**
 * Scrapes Dom and remove River.
 */
export function scrapeDom(includeNewsFeed: boolean): string {
    const root = document.getElementById("root");

    if (!root || !root.innerHTML) {
        return "";
    }

    /* Currently we don't have functionality to include experiences separately,
    because of that we do return whole dom if river included in render cache and exclude river otherwise */
    if (includeNewsFeed) {
        return root.innerHTML;
    }

    const rivers = document.getElementsByClassName(riverRcClassName);

    if (!rivers || !rivers.length) {
        return root.innerHTML;
    }

    const river = rivers[0] as HTMLElement;

    return root.innerHTML.replace(river.outerHTML, "");
}

/**
 * Gets the state object from render cache.
 * @returns The redux state object.
 */
export function getStateFromRenderCache(): any {
    // Check to see if this app instance was bootstrapped with a render-cache
    const renderCache = getRenderCache();

    if (!renderCache || !renderCache.state) {
        return null; // Render Cache missing or it does not include State.
    }

    let initialState;

    try {
        initialState = JSON.parse(renderCache.state, reduxStoreStringifyReviver);
    } catch (error) {
        // Cache state object is not parsable.
    }

    return initialState;
}

/**
 * Redux Store Stringify replacer HOF, generates store stringify.
 * @param storeExceptions list of store namespaces which will not be included in result.
 */
export function reduxStoreStringifyReplacer(key: string, value: any): any {
    const originalObject = this[key];
    if (originalObject instanceof Map) {
        return {
            dataType: "Map",
            value: [...originalObject]
        };
    } else {
        return value;
    }
}

/**
 * Custom JSON.stringify reviver that will create Map objects from a string/JSON representation
 * @param key not used
 * @param value The value being converted from a string
 */
function reduxStoreStringifyReviver(key: any, value: any): any {
    // If the value being restored is a object, check it it was a map
    if (typeof value === "object" && value !== null) {
        // If the object value being restored was a map, that was custom written, then revive it by creating a map
        if (value.dataType === "Map") {
            return new Map(value.value);
        }
    }

    // Return the original value being restored
    return value;
}